import {BrandVoices,type SoundCue} from './voices';
import {SoundScore,type SoundFrame} from './score';
import {sampleBrandLaunch} from './launch';
import {ambienceLevel} from './trailer';
import {translate} from '../locale';

const STORAGE='bb-cinema-sound';
/** Preserve the brand button voice, including the logo on the hero. */
export function uiClickCue(target:Element):SoundCue|null {
 const button=target.closest('button,a');
 if(!button||button.matches(':disabled,[aria-disabled="true"]')||button.closest('[data-video-player],.bb-video-player,.cinema-pause,.cinema-cue,.cinema-start,.cinema-sound,[data-cinema-link],[data-finale-object]'))return null;
 return {kind:'tap',gain:.6};
}
/** Lifecycle and consent only. The existing render loop supplies every picture frame. */
export class CinemaSound {
 private context:AudioContext|null=null;
 private voices:BrandVoices|null=null;
 private readonly score=new SoundScore();
 private readonly abort=new AbortController();
 private readonly button:HTMLButtonElement;
 private enabled=true;
 private destroyed=false;
 private lastFrame:SoundFrame|null=null;
 private runningWanted=false;
 private canResume=false;
 private resumePending:Promise<void>|null=null;
 private suspendTimer:number|undefined;
 private prepared:Promise<unknown>|null=null;
 private failed=false;
 private ducked=false;
 private launchAt=-Infinity;
 private readonly runButton:HTMLElement|null;
 private readonly audibleVideos=new Set<HTMLMediaElement>();
 private readonly recent:Array<{kind:string;clock:number;chapter:string}>=[];
 constructor(private readonly view:Window){
  const doc=view.document;
  this.runButton=doc.getElementById('runBtn');
  try{this.enabled=view.sessionStorage.getItem(STORAGE)!=='off';}catch{/* Storage is optional. */}
  this.button=doc.createElement('button');this.button.type='button';this.button.className='cinema-sound';
  this.button.innerHTML='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9h4l5-4v14l-5-4H4Z"/><path class="cinema-sound__waves" d="M17 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/><path class="cinema-sound__mute" d="m17 9 5 6m0-6-5 6"/></svg><span></span>';
  doc.body.append(this.button);this.updateLabel();
  const signal=this.abort.signal;
  this.button.addEventListener('click',()=>{void this.toggle();},{signal});
  const gesture=(event:Event)=>{
   if(event.target instanceof Element&&event.target.closest('.cinema-sound'))return;
   if(!this.enabled)return;
   void this.unlock();
   if(event.type==='click'&&event.target instanceof Element){
    const cue=uiClickCue(event.target);if(cue)this.play(cue);
   }
  };
  doc.addEventListener('pointerdown',gesture,{capture:true,signal});
  doc.addEventListener('click',gesture,{capture:true,signal});
  view.addEventListener('keydown',event=>{if(['Enter',' ','ArrowDown','PageDown'].includes(event.key))gesture(event);},{capture:true,signal});
  doc.addEventListener('touchend',gesture,{capture:true,passive:true,signal});
  doc.addEventListener('visibilitychange',()=>this.syncRunning(),{signal});
  view.addEventListener('bb:localechange',()=>this.updateLabel(),{signal});
  const videoChange=(event:Event)=>{
   const media=event.target;if(!(media instanceof HTMLMediaElement))return;
   if(!media.paused&&!media.ended&&!media.muted&&media.volume>0)this.audibleVideos.add(media);else this.audibleVideos.delete(media);
   this.ducked=this.audibleVideos.size>0;this.voices?.setDucking(this.ducked);
   if(this.ducked)this.voices?.setAmbience(0);
  };
  for(const name of ['play','playing','pause','ended','volumechange','emptied'])doc.addEventListener(name,videoChange,{capture:true,signal});
  view.addEventListener('online',()=>{void this.prepare();},{signal});
  // Decode before the film starts. A suspended context is a permission state,
  // not a failed soundtrack. No autoplay policy or saved mute is bypassed.
  void this.prepare().finally(()=>view.dispatchEvent(new Event('bb:sound-prepared')));
 }
 private updateLabel(){
  const ru=this.view.document.documentElement.lang.startsWith('ru');
  const waitingForGesture=this.enabled&&this.context?.state==='suspended'&&!this.lastFrame?.paused&&!this.view.document.hidden;
  const label=translate(this.failed?'Звук недоступен':this.enabled&&!waitingForGesture?'Выключить звук':'Включить звук');
  this.button.querySelector('span')!.textContent=label;this.button.setAttribute('aria-label',label);
  this.button.setAttribute('aria-pressed',String(this.enabled&&!waitingForGesture));this.button.dataset.enabled=String(this.enabled);
  this.button.dataset.audioState=this.context?.state??'uninitialized';
  this.button.dataset.sourcesReady=String(this.voices?.sourcesReady??false);
  this.button.title=ru?'Звук сайта. Видео управляются отдельно.':'Site sound. Videos have separate controls.';
 }
 private prepare():Promise<unknown>{
  if(this.destroyed)return Promise.resolve();
  try{
   if(!this.context){
    const Constructor=this.view.AudioContext??this.view.webkitAudioContext;
    if(!Constructor)throw new Error('Web Audio unavailable');
    this.context=new Constructor({latencyHint:'interactive'});this.voices=new BrandVoices(this.context);
    // Ask the browser once; an allowed reload may begin without a new gesture.
    this.canResume=true;
    this.context.addEventListener('statechange',()=>{
     if(this.context?.state==='running')this.canResume=true;
     // suspend/resume settle asynchronously. Reconcile the completed operation
     // against the latest picture state, not the state when it was requested.
     this.syncRunning(true);
     this.updateLabel();
    },{signal:this.abort.signal});
    this.syncRunning(true);
   }
   if(!this.prepared)this.prepared=this.voices!.loadSources().then(()=>{this.failed=false;this.updateLabel();}).catch(()=>{this.failed=true;this.updateLabel();}).finally(()=>{this.prepared=null;});
   return this.prepared;
  }catch{this.failed=true;this.updateLabel();return Promise.resolve();}
 }
 private async unlock(){
  if(this.destroyed||!this.enabled)return;
  void this.prepare();
  this.canResume=true;
  // Capture the trusted gesture even if the picture's pause flag updates next rAF.
  if(this.context&&!this.view.document.hidden){
   try{await this.context.resume();this.syncRunning(true);}catch{this.canResume=false;this.updateLabel();}
  }
 }
 private syncRunning(force=false){
  const picturePaused=this.view.document.hidden||!!this.lastFrame?.paused;
  this.voices?.setPaused(picturePaused);
  const wanted=this.enabled&&!picturePaused;
  if(!force&&wanted===this.runningWanted)return;this.runningWanted=wanted;
  const context=this.context;if(!context)return;
  this.voices?.setMuted(!wanted);
  if(wanted&&this.suspendTimer!==undefined){this.view.clearTimeout(this.suspendTimer);this.suspendTimer=undefined;}
  if(this.destroyed||context.state==='closed')return;
  if(wanted&&this.canResume&&context.state!=='running'&&!this.resumePending){
   this.resumePending=context.resume().catch(()=>{this.canResume=false;}).finally(()=>{this.resumePending=null;this.syncRunning(true);this.updateLabel();});
  }else if(!wanted&&context.state==='running'&&this.suspendTimer===undefined){
   // Suspending immediately freezes the gain before its fade and clicks at a
   // non-zero waveform sample. Let the audio thread finish the release first.
   this.suspendTimer=this.view.setTimeout(()=>{this.suspendTimer=undefined;if(!this.runningWanted)void context.suspend().catch(()=>{});},180);
  }
 }
 async toggle(){
  if(this.enabled&&this.context?.state==='suspended'&&!this.lastFrame?.paused&&!this.view.document.hidden){await this.unlock();this.updateLabel();return;}
  this.enabled=!this.enabled;this.failed=false;
  try{this.view.sessionStorage.setItem(STORAGE,this.enabled?'on':'off');}catch{/* Storage is optional. */}
  if(this.enabled)await this.unlock();else{this.voices?.clear(.12);this.syncRunning();}
  this.updateLabel();if(this.enabled)this.play({kind:'check',gain:.5});
 }
 private play(cue:SoundCue){
  if(!this.enabled||!this.voices||this.context?.state!=='running'||this.ducked||this.lastFrame?.chapter==='uses-video'||this.view.document.hidden||this.lastFrame?.paused)return;
  this.voices.play(cue);this.recent.push({kind:cue.kind,clock:this.lastFrame?.clock??0,chapter:this.lastFrame?.chapter??'hero'});if(this.recent.length>80)this.recent.shift();
 }
 frame(frame:SoundFrame){
  if(this.destroyed)return;
  const doc=this.view.document;
  if(frame.chapter==='film')frame={...frame,typing:[{id:'film',length:doc.getElementById('aiInput')?.textContent?.length??0,pressed:doc.getElementById('sendBtn')?.classList.contains('is-press')??false,pan:0}]};
  else if(frame.chapter==='workflow')frame={...frame,typing:Array.from(doc.querySelectorAll<HTMLElement>('#workflow [data-composer]')).map((n,i)=>({id:`chat-${i}`,length:n.querySelector('[data-composer-draft]')?.textContent?.length??0,pressed:n.querySelector('[data-composer-send]')?.hasAttribute('data-pressed')??false,automatic:n.hasAttribute('data-bridge-draft'),pan:i===0?-.4:.4}))};
  const changed=this.lastFrame&&frame.visit!==this.lastFrame.visit;
  if(changed){
   // Natural chapter handoffs retain bounded musical tails. Direct navigation
   // fades them out; neither path slices the previous sound at a 15ms boundary.
   if(frame.phase==='hero'||frame.chapter===this.lastFrame?.chapter||frame.clock<(this.lastFrame?.clock??0))this.voices?.clear(.7);
   this.launchAt=-Infinity;
  }
  this.lastFrame=frame;this.syncRunning();
  if(frame.position)this.voices?.setPosition(frame.position);
  this.voices?.setDucking(this.ducked||frame.chapter==='uses-video');
  const cues=this.score.sample(frame);
  if(cues.some(cue=>cue.kind==='start'))this.launchAt=frame.clock;
  const launch=sampleBrandLaunch(frame.clock-this.launchAt);
  if(this.runButton){this.runButton.dataset.launch=String(launch.energy>0);this.runButton.style.setProperty('--launch-energy',String(launch.energy));this.runButton.style.setProperty('--launch-orange',String(launch.orange));}
  this.voices?.setAmbience(this.enabled&&!this.ducked?ambienceLevel(frame):0);
  for(const cue of cues)this.play(cue);
 }
 get snapshot(){return{enabled:this.enabled,unlocked:!!this.context,state:this.context?.state??'locked',sourcesReady:this.voices?.sourcesReady??false,ducked:this.ducked,failed:this.failed,activeVoices:this.voices?.activeVoices??0,recent:[...this.recent]};}
 get ready(){return this.prepare();}
 destroy(){
  this.destroyed=true;if(this.suspendTimer!==undefined)this.view.clearTimeout(this.suspendTimer);this.abort.abort();this.button.remove();this.voices?.destroy();void this.context?.close();
  if(this.view.__BB_SOUND__===this)delete this.view.__BB_SOUND__;
 }
}
declare global{interface Window{AudioContext:typeof AudioContext;webkitAudioContext?:typeof AudioContext;__BB_SOUND__?:CinemaSound}}
export function mountCinemaSound(view:Window){return view.__BB_SOUND__??(view.__BB_SOUND__=new CinemaSound(view));}
